import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Building2, Search, AlertTriangle } from 'lucide-react';
import { correctArticleCompany } from '../lib/api/articles.js';
import { listEntities } from '../lib/api/entities.js';
import { getArticlePublishTargets } from '../lib/api/egress.js';
import type { EntitySummary } from '../lib/api/types.js';
import { Modal, ModalHeader, ModalBody, ModalFooter } from './ui/modal.js';

interface CorrectCompanyDialogProps {
  isOpen: boolean;
  onClose: () => void;
  articleId: string;
  /** Optimistic-lock token — the article's updated_at at edit-start. */
  expectedUpdatedAt: string | null;
  /** Current announcement badge + entity, for the discrepancy display. */
  currentCompanyCode?: string | null;
  currentExchangeCode?: string | null;
  currentEntityName?: string | null;
  currentEntityTicker?: string | null;
  onSuccess: () => void;
}

/**
 * Re-bind an article's source announcement to the correct entity. The
 * published badge ("ASX: …") is rendered from the announcement's
 * company_code, NOT the article — so a wrong ticker can only be fixed by
 * correcting the announcement's entity binding. This dialog lets an operator
 * search for the correct (existing, Cynapse-synced) entity and re-bind;
 * the backend updates Assayer + Cynapse and re-publishes affected articles.
 */
export function CorrectCompanyDialog({
  isOpen,
  onClose,
  articleId,
  expectedUpdatedAt,
  currentCompanyCode,
  currentExchangeCode,
  currentEntityName,
  currentEntityTicker,
  onSuccess,
}: CorrectCompanyDialogProps) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<EntitySummary | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Impact summary: how many live targets this re-publish will touch.
  const { data: publishTargets } = useQuery({
    queryKey: ['article-publish-targets', articleId],
    queryFn: () => getArticlePublishTargets(articleId),
    enabled: isOpen,
  });

  // Entity search (ticker or name). Only run once the operator has typed
  // a couple of characters; the backend matches canonical_identifier/display_name.
  const { data: entityResults, isFetching } = useQuery({
    queryKey: ['entity-search', search],
    queryFn: () => listEntities({ search: search.trim(), page_size: 10 }),
    enabled: isOpen && search.trim().length >= 2,
  });

  const handleSubmit = async () => {
    if (!selected) return;
    setIsSubmitting(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const result = await correctArticleCompany(
        articleId,
        { target_entity_id: selected.entity_id },
        expectedUpdatedAt,
      );
      const cyn = result.cynapse?.status;
      const cynNote =
        cyn === 'synced'
          ? 'Cynapse updated.'
          : cyn === 'not_applicable'
          ? 'No Cynapse binding to update.'
          : 'Cynapse update FAILED — re-run to retry.';
      setSuccessMessage(
        `Re-bound to ${result.to_company_code}. Re-published ${result.articles_republished} article(s). ${cynNote}`,
      );
      onSuccess();
      queryClient.invalidateQueries({ queryKey: ['article', articleId] });
      setTimeout(() => {
        onClose();
        setSuccessMessage(null);
        setSelected(null);
        setSearch('');
      }, 2200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to correct company');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const targetCount = publishTargets?.length ?? 0;

  return (
    <Modal onClose={onClose} canClose={!isSubmitting} size="lg">
      <ModalHeader>
        <div className="flex items-center space-x-3">
          <Building2 className="h-6 w-6 text-primary-600" />
          <h2 className="text-xl sm:text-2xl font-bold text-grey-900">Correct company / ticker</h2>
        </div>
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="text-grey-400 hover:text-grey-600 transition-colors disabled:opacity-50 touch-target"
        >
          <X className="h-6 w-6" />
        </button>
      </ModalHeader>

      <ModalBody>
          {/* Current state */}
          <div className="mb-5 p-4 bg-grey-50 border border-grey-200 rounded-lg text-sm">
            <div className="text-grey-600">
              Published badge:{' '}
              <span className="font-mono font-bold text-grey-900">
                {currentExchangeCode || 'ASX'}: {currentCompanyCode || '—'}
              </span>
            </div>
            <div className="text-grey-600 mt-1">
              Bound entity:{' '}
              <span className="font-semibold text-grey-900">
                {currentEntityName || '—'}
                {currentEntityTicker ? ` (${currentEntityTicker})` : ''}
              </span>
            </div>
          </div>

          <p className="text-grey-600 mb-4 text-sm">
            Search for the correct entity. Re-binding updates the source
            announcement (the badge), heals Cynapse, and re-publishes the
            affected article(s).
          </p>

          {error && (
            <div className="mb-4 p-4 bg-danger-50 border-2 border-danger-200 text-danger-700 rounded-lg">
              <p className="font-semibold">{error}</p>
            </div>
          )}
          {successMessage && (
            <div className="mb-4 p-4 bg-success-50 border-2 border-success-200 text-success-700 rounded-lg">
              <p className="font-semibold">{successMessage}</p>
            </div>
          )}

          {/* Entity search */}
          <div className="mb-4">
            <label className="block text-base font-bold text-grey-900 mb-2">
              Correct entity
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setSelected(null); }}
                placeholder="Search by ticker or name (e.g. MRT or Maritana)"
                className="w-full border-2 border-grey-200 rounded-lg pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {search.trim().length >= 2 && (
              <div className="mt-2 space-y-2 max-h-56 overflow-y-auto">
                {isFetching && <p className="text-sm text-grey-500 px-1">Searching…</p>}
                {!isFetching && (entityResults?.items?.length ?? 0) === 0 && (
                  <p className="text-sm text-grey-500 px-1">No matching entities.</p>
                )}
                {entityResults?.items?.map((ent) => (
                  <label
                    key={ent.entity_id}
                    className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      selected?.entity_id === ent.entity_id
                        ? 'border-primary-500 bg-primary-50 shadow-sm'
                        : 'border-grey-200 hover:border-primary-300 hover:bg-grey-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="entity"
                      checked={selected?.entity_id === ent.entity_id}
                      onChange={() => setSelected(ent)}
                      className="mt-1 mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="font-bold text-grey-900">
                        {ent.display_name}{' '}
                        <span className="font-mono text-grey-600">({ent.canonical_identifier})</span>
                      </div>
                      <div className="text-xs text-grey-500 mt-0.5">
                        {ent.entity_type} · {ent.approval_status}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Impact summary */}
          {selected && (
            <div className="mb-2 p-3 bg-warning-50 border border-warning-200 rounded-lg text-sm text-warning-800 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>
                Re-binds the source announcement to{' '}
                <strong>{selected.display_name} ({selected.canonical_identifier})</strong> and
                re-publishes the article{targetCount > 1 ? `s across ${targetCount} targets` : ''}.
                The live badge updates after promotion.
              </span>
            </div>
          )}

      </ModalBody>

      <ModalFooter>
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="px-6 py-2.5 border-2 border-grey-300 text-grey-700 font-semibold rounded-lg hover:bg-grey-50 hover:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !selected}
          className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center space-x-2"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              <span>Correcting…</span>
            </>
          ) : (
            <>
              <Building2 className="h-4 w-4" />
              <span>Re-bind &amp; re-publish</span>
            </>
          )}
        </button>
      </ModalFooter>
    </Modal>
  );
}
